import { elementInList, getNestedSet, flatten, getSet, jsonCopyObject, listsShareSomeElement, setIntersection, removePropertyFromObjectList, addPropertyToObjectList } from "../../globals/helpers/helpers"
import { DateSchedule, Program, Session } from "../../globals/store/programs.data"
import { Exhibitor, ExhibitorStand } from "../../globals/store/exhibitors.data"
import { OnsiteAccess, OnsiteActivity, OnsiteClear, OnsiteSession, OnsiteSingleTimeline, OnsiteTimelineBrick } from "../../globals/store/onsite.data"
import { getProgramIdsByExhibitorIds } from "../../globals/helpers/program.helper"
import { filterFilters, FilterGroup, filterTypes } from "../../globals/store/filters.data"
import { LabelMaps } from "../../globals/store/events.data"
import { WFWorker, WorkforceGroup } from "../../globals/store/workforce.data"
import { JoiResource } from "../../globals/store/resources.data"
import { Performer } from "../../globals/store/performers.data"
import { Sponsor } from "../../globals/store/sponsors.data"
import { Attendee } from "../../globals/store/attendees.data"
import { sortOnsiteTimelineBrick } from "../../globals/store/reselectors/onsite.reselector.filtering"
import { ShareOSSInfo } from "../../globals/store/share.data"

export const filterSponsorsByExcludedOSSFieldsAndFilters = (info: ShareOSSInfo, sponsors: { [sId: string]: Sponsor } = {}, filters: FilterGroup): { [sId: string]: Sponsor } => {
  sponsors = jsonCopyObject(sponsors)
  const excludedFieldIds = info.excludedFieldIds || []
  if (excludedFieldIds.includes('sponsors')) {
    sponsors = {}
  } else {
    const sponsorsList = Object.values(sponsors).filter(p => !filters || !filters.sponsors || filters.sponsors.includes(p.id)) // this retains the "original" (actually copied above by jsonCopyObject) performer objects, so modifying them modifies performers.  The filtering only matters for the public share version of this.
    excludedFieldIds.forEach(fieldId => {
      switch (fieldId) {
        case 'sponsorProfileType':
          removePropertyFromObjectList(sponsorsList, 'sponsorTypeId')
          break;
        case 'sponsorLabels':
          addPropertyToObjectList(sponsorsList, 'hideLabels', true)
          break;
        case 'sponsorProfileDescription':
          removePropertyFromObjectList(sponsorsList, 'description')
          break;
        case 'sponsorProfileSocial':
          removePropertyFromObjectList(sponsorsList, 'social')
          break;
        case 'sponsorProfilecallToAction':
          removePropertyFromObjectList(sponsorsList, 'callToAction')
          removePropertyFromObjectList(sponsorsList, 'callToActionUrl')
          break;
        case 'sponsorProfileLogoUrl':
          removePropertyFromObjectList(sponsorsList, 'logoUrl')
          removePropertyFromObjectList(sponsorsList, 'fullLogoUrl')
          addPropertyToObjectList(sponsorsList, 'hideLogo', true)
          break;
      }
    })
  }
  return sponsors
}


export const filterShareableByExcludedOSSFields = (info: ShareOSSInfo): ShareOSSInfo => {
  info = jsonCopyObject(info)
  const excludedFieldIds = info.excludedFieldIds || []
  excludedFieldIds.forEach(fieldId => {
    switch (fieldId) {
      case 'eventName':
        delete info.eventTitle
        break;
      case 'publishedName':
        delete info.title
        break;
      case 'publishedDescription':
        delete info.description
        break;
      default:
        break;
    }
  })

  // no need to return the below, might be better not to
  delete info.filters
  delete info.excludedFieldIds
  delete info.id
  delete info.status

  // console.log({ info })
  return info
}


export const filterPerformersByExcludedOSSFieldsAndFilters = (info: ShareOSSInfo, performers: { [pId: string]: Performer } = {}, filters: FilterGroup): { [pId: string]: Performer } => {
  performers = jsonCopyObject(performers)
  const excludedFieldIds = info.excludedFieldIds || []
  if (excludedFieldIds.includes('performers')) {
    performers = {}
  } else {
    const performersList = Object.values(performers).filter(p => !filters || !filters.performers || filters.performers.includes(p.id)) // this retains the "original" (actually copied above by jsonCopyObject) performer objects, so modifying them modifies performers.  The filtering only matters for the public share version of this.
    excludedFieldIds.forEach(fieldId => {
      switch (fieldId) {
        case 'performerName':
          removePropertyFromObjectList(performersList, 'name')
          break;
        case 'line1':
          removePropertyFromObjectList(performersList, 'line1')
          break;
        case 'line2':
          removePropertyFromObjectList(performersList, 'line2')
          break;
        case 'performerLabels':
          addPropertyToObjectList(performersList, 'hideLabels', true)
          break;
        case 'bio':
          removePropertyFromObjectList(performersList, 'bio')
          break;
        case 'social':
          removePropertyFromObjectList(performersList, 'social')
          break;
        case 'photoUrl':
          removePropertyFromObjectList(performersList, 'photoUrl')
          removePropertyFromObjectList(performersList, 'fullPhotoUrl')
          addPropertyToObjectList(performersList, 'photoHidden', true)
          break;
        case 'logoUrl':
          removePropertyFromObjectList(performersList, 'logoUrl')
          removePropertyFromObjectList(performersList, 'fullLogoUrl')
          break;
        default:
          break;
      }
    })
  }

  // console.log({ performers, excludedFieldIds })
  return performers
}

export const filterTimelineByFields = (excludedFieldIds: String[] = [], timeline: OnsiteSingleTimeline[]): OnsiteSingleTimeline[] => {

  timeline = jsonCopyObject(timeline)
  // console.log({ timeline: jsonCopyObject(timeline) })
  const bricks: OnsiteTimelineBrick[] = flatten(timeline.map(t => t.bricks))
  
  const activityBricks = bricks.map(b => b.type === 'activity' ? b : null).filter(b => b)
  const sessionBricks = bricks.map(b => b.type === 'session' ? b : null).filter(b => b)

  excludedFieldIds.forEach(fieldId => {
    // going for clarity over performance & succintness here
    switch (fieldId) {
      case 'eventDate':
        timeline.forEach((dayTimeline, i) => dayTimeline.dayDate = `Day ${i+1}`)
        break;
      case 'aPrograms':
        removePropertyFromObjectList(activityBricks, 'program_title')
        removePropertyFromObjectList(activityBricks, 'program_location')
        addPropertyToObjectList(activityBricks, 'hideVenueName', true)
        addPropertyToObjectList(activityBricks, 'hideVenueAddress', true)
        break;
      case 'aProgramName':
        removePropertyFromObjectList(activityBricks, 'program_title')
        break;
      case 'aLocation':
        removePropertyFromObjectList(activityBricks, 'program_location')
        break;
      case 'aVenue':
        addPropertyToObjectList(activityBricks, 'hideVenueName', true)
        break;
      case 'aAddress':
        addPropertyToObjectList(activityBricks, 'hideVenueAddress', true)
        break;
      case 'sPrograms':
        removePropertyFromObjectList(sessionBricks, 'program_title')
        removePropertyFromObjectList(sessionBricks, 'program_location')
        addPropertyToObjectList(sessionBricks, 'hideVenueName', true)
        addPropertyToObjectList(sessionBricks, 'hideVenueAddress', true)
        break;
      case 'sProgramName':
        removePropertyFromObjectList(sessionBricks, 'program_title')
        break;
      case 'sLocation':
        removePropertyFromObjectList(sessionBricks, 'program_location')
        break;
      case 'sVenue':
        addPropertyToObjectList(sessionBricks, 'hideVenueName', true)
        break;
      case 'sAddress':
        addPropertyToObjectList(sessionBricks, 'hideVenueAddress', true)
        break;
      
      case 'sessionTitle':
        removePropertyFromObjectList(sessionBricks, 'title')
        break;
      case 'sessionDescription':
        removePropertyFromObjectList(sessionBricks, 'description')
        break;
      case 'publicLinks':
        removePropertyFromObjectList(sessionBricks, 'publicLinks')
        break;
      case 'sessionLabels':
        removePropertyFromObjectList(sessionBricks, 'labels')
        break;

      case 'activityName':
        removePropertyFromObjectList(activityBricks, 'title')
        removePropertyFromObjectList(activityBricks, 'schedule_title')
        break;
      case 'activityDescription':
        removePropertyFromObjectList(activityBricks, 'description')
        break;
      case 'activityLabels':
        removePropertyFromObjectList(activityBricks, 'labels')
        break;
      case 'activityStatus':
        removePropertyFromObjectList(activityBricks, 'completed')
        break;
  

      default:
        break;
    }
  })
  return timeline
}




export const getOnsiteTimelineSets = (multiTimelines: DateSchedule[], exhibitors: {[id:string]: Exhibitor}, exhibitorStands: { [sId: string]: ExhibitorStand }, includeTags: boolean = true) => {

  const programs = flatten(multiTimelines.map(t => t.programs))
  const sessionsAndActivities = flatten(programs.map(p => p.sessions)).filter(s => s.type !== 'access' && s.type !== 'clear') as (OnsiteSession | OnsiteActivity)[]
  // console.log({ multiTimelines: jsonCopyObject(multiTimelines) })

  const allActivities = sessionsAndActivities.map(s => s.type === 'activity' ? s : null).filter(s => s)
  const allSessions = sessionsAndActivities.map(s => s.type === 'session' ? s : null).filter(s => s)

  const programLabels = getNestedSet(programs, 'labels')
  const groupTags = getNestedSet(sessionsAndActivities, 'groupTags')
  const tags = getNestedSet(sessionsAndActivities, 'tags') // workforceTags
  
  const performerIds = getNestedSet(sessionsAndActivities, 'performerIds') // includes activity tags
  const performerSessionTags = getNestedSet(allSessions, 'performerTags')
  performerSessionTags.forEach(t => performerIds.add(t))

  const sponsorIds = getNestedSet(sessionsAndActivities, 'sponsorIds') // includes activity tags
  const sponsorSessionTags = getNestedSet(allSessions, 'sponsorTags')
  sponsorSessionTags.forEach(t => sponsorIds.add(t))

  const resourceIds = getNestedSet(allActivities, 'resourceIds') // includes activity tags
  const resourceSessionTags = getNestedSet(allSessions, 'resourceTags')
  resourceSessionTags.forEach(t => resourceIds.add(t))

  const attendeeIds = getNestedSet(allActivities, 'attendeeIds') // includes activity tags
  const attendeeSessionTags = getNestedSet(allSessions, 'attendeeTags')
  attendeeSessionTags.forEach(t => attendeeIds.add(t))
  
  if (includeTags) {
    const programWorkforceTags = getNestedSet(programs, 'workforceTags')
    programWorkforceTags.forEach(t => tags.add(t))
    const performerProgramTags = getNestedSet(programs, 'performerTags')
    performerProgramTags.forEach(t => performerIds.add(t))
    const sponsorProgramTags = getNestedSet(programs, 'sponsorTags')
    sponsorProgramTags.forEach(t => sponsorIds.add(t))
    const resourceProgramTags = getNestedSet(programs, 'resourceTags')
    resourceProgramTags.forEach(t => resourceIds.add(t))
    const attendeeProgramTags = getNestedSet(programs, 'attendeeTags')
    attendeeProgramTags.forEach(t => attendeeIds.add(t))
  }

  const sessions = sessionsAndActivities.filter(s => s.type === 'session')
  const sessionLabels = getNestedSet(sessions, 'labels')

  const activityLabels = getNestedSet(allActivities, 'labels')

  const programIds = getSet(programs.filter(p => p.subType.includes('program')), 'scheduleId')
  const scheduleIds = getSet(programs.filter(p => p.subType.includes('standalone')), 'scheduleId')

  const activityExhibitorIds = getNestedSet(allActivities, 'exhibitorIds')
  const sessionExhibitorTags = getNestedSet(allSessions, 'exhibitorTags')
  const programExhibitorTags = getNestedSet(programs, 'exhibitorTags')
  const pIdsByEIds = getProgramIdsByExhibitorIds(exhibitorStands)

  const possibleExhibitors = !exhibitors ? [] : Object.values(exhibitors).filter(({ id }) => 
    activityExhibitorIds.has(id) || 
    sessionExhibitorTags.has(id) || 
    (includeTags && programExhibitorTags.has(id)) || 
    (!!pIdsByEIds[id]?.length && (!includeTags || !programExhibitorTags.size) && Array.from(programIds).some(programId => pIdsByEIds[id].includes(programId)))) // not true filtering, just finding which ones COULD be on a timeline

  const exhibitorIds = getSet(possibleExhibitors, 'id')

  return { programLabels, sessionLabels, activityLabels, programIds, scheduleIds, groupTags, tags, performerIds, sponsorIds, exhibitorIds, programs, resourceIds, attendeeIds }
}


export const filterOnsiteTimelineFilters = (
  filters: FilterGroup, multiTimelines: DateSchedule[], performers: { [pId: string]: Performer },
  labelMaps: LabelMaps, workforceGroupsById: { [id:string]: WorkforceGroup }, 
  workersByEmail: { [key:string]: WFWorker }, sponsors: { [sId: string]: Sponsor }, 
  exhibitors: { [eId:string]: Exhibitor }, exhibitorStands: { [sId: string]: ExhibitorStand },
  resources: { [rId:string]: JoiResource }, attendees: { [aId: string]: Attendee } ): FilterGroup => {
    const { programLabels, sessionLabels, activityLabels, groupTags, tags, programs } = getOnsiteTimelineSets(multiTimelines, exhibitors, exhibitorStands)  
    // I can't remember exactly why I can't just pull these from the getOnsiteTimelineSets function above.  Something to do with performer ids being present in sessions / activities / maybe program tags, but no longer on the event
    const performerIds: Set<string> = !performers ? new Set() : new Set(Object.keys(performers))
    const sponsorIds: Set<string> = !sponsors ? new Set() : new Set(Object.keys(sponsors))
    const exhibitorIds: Set<string> = !exhibitors ? new Set() : new Set(Object.keys(exhibitors))
    const resourceIds: Set<string> = !resources ? new Set() : new Set(Object.keys(resources))
    const attendeeIds: Set<string> = !attendees ? new Set() : new Set(Object.keys(attendees))

    // console.log({ programLabels, sessionLabels, activityLabels, groupTags, tags, performerIds, filters })
    const programIds = getSet(programs, 'scheduleId')
    const scheduleIds = getSet(flatten<any>(programs.map(p => p.sessions.filter(s => s.type === 'activity'))), 'linkId')
    const { session_labels, activity_labels, program_labels } = labelMaps

    filterFilters({ filters, workforceGroupsById, workersByEmail, session_labels, activity_labels, program_labels, programIds, sessionLabels, programLabels, activityLabels, tags, groupTags, performerIds, scheduleIds, sponsorIds, exhibitorIds, resourceIds, attendeeIds })
    return filters
}



const checkIdsAndTags = (type: 'performer' | 'exhibitor' | 'sponsor' | 'resource' | 'attendee', filters: FilterGroup, sOA) => {
  const filter = filters[`${type}s`]
  const ids = sOA[`${type}Ids`]
  const tags = sOA[`${type}Tags`]
  return (filter && filter.length && ((ids && ids.some(id => filter.includes(id))) || (tags && tags.some(tag => filter.includes(tag)))))
}

const isTaggedOrHasId = (sOA, filters: FilterGroup) => {
  const tagged = (filters.tags && sOA.tags && sOA.tags.some(t => filters.tags.includes(t))) ||
    (filters.groupTags && sOA.groupTags && sOA.groupTags.some(g => filters.groupTags.includes(g))) ||
    checkIdsAndTags('performer', filters, sOA) ||
    checkIdsAndTags('exhibitor', filters, sOA) ||
    checkIdsAndTags('sponsor', filters, sOA) ||
    checkIdsAndTags('resource', filters, sOA) ||
    checkIdsAndTags('attendee', filters, sOA)
  return tagged
}

const noTagsIds = filters=> {
  const notTagged = !filters.tags && !filters.groupTags && !filters.performers && !filters.sponsors && !filters.exhibitors && !filters.resources && !filters.attendees
  return notTagged
}

const filterSessionOrActivity = <SOA extends Session | OnsiteActivity>(sOA: SOA, filters: FilterGroup, labelFilters: string[]) => {
  const hasLabelFilters = !!labelFilters?.length
  const activelyPassesTags = !!isTaggedOrHasId(sOA, filters)
  const activelyPassesSessionLabels = hasLabelFilters && sOA.labels && sOA.labels.some(l => labelFilters.includes(l)) 
  const activelyPasses = activelyPassesTags || activelyPassesSessionLabels
  const doesntNeedToActivelyPass = noTagsIds(filters) && !hasLabelFilters
  // console.log(`${sOA.title}`, { sOA, filters, activelyPasses, activelyPassesTags, doesntNeedToActivelyPass })
  return doesntNeedToActivelyPass || activelyPasses 
}

const filterSessions = (sessions: OnsiteSession[], filters: FilterGroup) => sessions
  .filter(s => filterSessionOrActivity(s, filters, filters.sessionLabels))

const filterActivities = (activities: OnsiteActivity[], filters: FilterGroup, programsAreFiltered: boolean, isStandalone: boolean) => activities
  .filter(a => {
    const standaloneActivelySelected = !!filters.programs?.includes(a.scheduleId) && isStandalone
    const subScheduleActivelySelected = !!filters.schedules?.includes(a.linkId) && !isStandalone
    const hasScheduleFiltering = !!filters.programs?.length || !!filters.schedules?.length

    const scheduleSelectedButNotThisOne = programsAreFiltered && !standaloneActivelySelected && !subScheduleActivelySelected
    // if (a.id === '9hmra1Vy3j2qHZwUY33S') console.log(`Activity ${a.title}`, a, { standaloneActivelySelected, subScheduleActivelySelected, scheduleSelectedButNotThisOne, programsAreFiltered })

    if (hasScheduleFiltering && scheduleSelectedButNotThisOne) return false
    return filterSessionOrActivity(a, filters, filters.activityLabels)
  }
)

const programHasTag = (program: Program, filters: FilterGroup): boolean => [
  listsShareSomeElement(program.performerTags, filters.performers),
  listsShareSomeElement(program.workforceTags, filters.tags),
  listsShareSomeElement(program.sponsorTags, filters.sponsors),
  listsShareSomeElement(program.exhibitorTags, filters.exhibitors),
  listsShareSomeElement(program.resourceTags, filters.resources),
  listsShareSomeElement(program.attendeeTags, filters.attendees)
].some(passes => passes)

export const filterTimeline = (multiTimelines: DateSchedule[], fullFilters: FilterGroup, filterPerformerIds = true, exhibitors: { [eId:string]: Exhibitor } = {}, type: 'oss' | 'mySchedule' | 'exhibitorBrief' | 'performerBrief' | 'resourceBrief' | 'attendeeBrief', stands: { [sId: string]: ExhibitorStand }, programsMap: { [pId: string]: Program }): DateSchedule[] => {

  multiTimelines = jsonCopyObject(multiTimelines)
  // console.log(multiTimelines)
  const isEntityBrief = type.includes('Brief');
  const { performerIds, sponsorIds, exhibitorIds, resourceIds, tags, attendeeIds } = getOnsiteTimelineSets(multiTimelines, exhibitors, stands, true)
  const filters: FilterGroup = { 
    ...fullFilters,
    sponsors: !fullFilters.sponsors || !sponsorIds.size ? null : Array.from(setIntersection(sponsorIds, new Set(fullFilters.sponsors))), 
    tags: type === 'mySchedule' ? Array.from(fullFilters.tags) : 
          !fullFilters.tags || !tags.size ? null : Array.from(setIntersection(tags, new Set(fullFilters.tags))),
    exhibitors: !fullFilters.exhibitors || !exhibitorIds.size ? null : Array.from(setIntersection(exhibitorIds, new Set(fullFilters.exhibitors))),
    performers: !fullFilters.performers || !performerIds.size ? null : Array.from(setIntersection(performerIds, new Set(fullFilters.performers))),
    resources: !fullFilters.resources || !resourceIds.size ? null : Array.from(setIntersection(resourceIds, new Set(fullFilters.resources))),
    attendees: !fullFilters.attendees || !attendeeIds.size ? null : Array.from(setIntersection(attendeeIds, new Set(fullFilters.attendees))),
  } // remember, the filters coming in here may have been filtered by existing performers, sponsors, etc. so you want to make sure to take the intersection of the two sets 
  // console.log({ filters, multiTimelines, fullFilters })
  let exhibitorPrograms: Set<string>
  if (exhibitors && (!!filters.exhibitors?.length || !!filters.exhibitorLabels?.length)) {
    const exhiboIds = getSet(Object.values(exhibitors).filter(e => 
        (filters.exhibitors && filters.exhibitors.includes(e.id)) || 
        (filters.exhibitorLabels && filters.exhibitorLabels.includes(e.id))
      ), 'id')
    exhibitorPrograms = !stands ? null : getSet(
      Object.values(stands).filter(s => s.programId && s.exhibitorId && exhiboIds.has(s.exhibitorId)), 'programId'
    )
  }

  filters.count = Object.entries(filters).filter(([k,f]) => k !== filterTypes.count && f)
                                         .reduce((total, [ _, f ]) => total + f.length, 0)
                      
  // console.log({ filtersCount: filters.count, type })
  if (filters.count === 0) return type.includes('Brief') ? [] : multiTimelines
  const exhibitorFiltersExist = !!filters.exhibitors || !!filters.exhibitorLabels
  const allPrograms = flatten(multiTimelines.map(m => m.programs))
  const someProgramIsTagged = allPrograms.map(p => programsMap[p.scheduleId]).filter(p => !!p).some(p => programHasTag(p, filters))
  const subscheduleIdsByProgramId: { [pId: string]: Set<string> } = {}
  const programIdBySubscheduleId: { [sId: string]: string } = {}
  const nonStandalonePrograms = allPrograms.filter(p => !p.subType.includes('standalone'))
  nonStandalonePrograms.forEach(p => subscheduleIdsByProgramId[p.scheduleId] = new Set()) // sets the same thing multiple times, but that's ok
  nonStandalonePrograms.forEach(p => p.sessions.filter(s => s.type === 'activity').forEach((a: OnsiteActivity) => {
    subscheduleIdsByProgramId[p.scheduleId].add(a.linkId)
    programIdBySubscheduleId[a.linkId] = p.scheduleId
  }))
  multiTimelines = multiTimelines.map(multiTimeline => {
    // console.log({ multiTimeline, programs: multiTimeline.programs, filters })
    // console.log({ mPrograms: multiTimeline.programs })
    const programs = multiTimeline.programs.map(p => {
      const fullProgram = programsMap[p.scheduleId]
      const isStandalone = p.subType.includes('standalone')
      const programTagged = !isStandalone && fullProgram && programHasTag(fullProgram, filters)
      const sessions = p.sessions.filter(s => s.type === 'session') as OnsiteSession[]
      const activities = p.sessions.filter(s => s.type === 'activity') as OnsiteActivity[]

      const hasThisProgramFilter = elementInList(p.scheduleId, filters.programs)
      const programSubScheduleSelected = !isStandalone && filters?.schedules?.some(sId => subscheduleIdsByProgramId[p.scheduleId].has(sId))
      const exhibitorOnProgramSchedule = !!exhibitorPrograms?.size && p.subType === 'program' && exhibitorPrograms.has(p.scheduleId)

      const hasThisProgramLabel = listsShareSomeElement(filters.programLabels, p.labels)

      const programActivelySelected = hasThisProgramFilter || programSubScheduleSelected || hasThisProgramLabel || exhibitorOnProgramSchedule || programTagged

      const programSessionOrActivityTagged = sessions.some(s => isTaggedOrHasId(s, filters)) || activities.some(a => isTaggedOrHasId(a, filters))
      const someProgramIsTaggedButNotThisOne = !!someProgramIsTagged && !programSessionOrActivityTagged
      const programsAreFiltered = !!exhibitorPrograms?.size || !!filters?.programLabels?.length || someProgramIsTaggedButNotThisOne || !!filters.programs?.length || exhibitorFiltersExist || !!filters?.schedules?.length
            
      const programFilteredOut = !isStandalone && programsAreFiltered && !programActivelySelected
      // console.log(p.title, { isStandalone, programsAreFiltered, programActivelySelected, programFilteredOut, programTagged, fullProgram })
      if (programFilteredOut) return null // if it is entity brief, activities will always be filtered by the entityId, and sessions likewise (except exhibitors on program or programs tagged), so can ignore programFilteredOut here

      const filteredSessions = programTagged || exhibitorOnProgramSchedule ? [...sessions] : 
        programSubScheduleSelected && !hasThisProgramFilter ? [] :
        !!filters.activityLabels?.length && !filters.sessionLabels?.length && !hasThisProgramLabel ? [] :
        filterSessions(sessions, filters)
      const filteredActivities = !!filters.sessionLabels?.length && !filters.activityLabels?.length ? [] : 
        filterActivities(activities, filters, programsAreFiltered, isStandalone)

      const allAccessClears = p.sessions.filter(s => ['access', 'clear'].includes(s.type)) as (OnsiteAccess | OnsiteClear)[]

      const accessClears = programActivelySelected ? allAccessClears : []

      // (!programsAreFiltered && !isEntityBrief) || (programsAreFiltered && programActivelySelected) || programTagged ? allAccessClears : []
      
      // console.log(p.title, { programTagged, programsAreFiltered, programActivelySelected, accessClears, type, isEntityBrief, allAccessClears, p })
      
      const sessionsAndActivities = [...filteredActivities, ...filteredSessions, ...accessClears]
      sessionsAndActivities.sort(sortOnsiteTimelineBrick)

      const newProgram = {
        ...p,
        sessions: sessionsAndActivities
      }

      // if (activities.some(a => a.id === '9hmra1Vy3j2qHZwUY33S')) console.log({ activities, filteredActivities, filters, hasThisProgramFilter, exhibitorFiltersExist, filteredSessions, sessionsAndActivities, programActivelySelected, programsAreFiltered, exhibitorPrograms, someProgramIsTagged, programTagged, programSessionOrActivityTagged })

      if (!newProgram.sessions.length) return null
      return newProgram
    })
    // console.log({ filteredPrograms: programs })
    return { ...multiTimeline, programs: programs.filter(p => !!p) }
  })

  return multiTimelines.filter(({ programs }) => programs.some(p => p.sessions.length))
}




